import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../services/api';

export default function CollaborationPage() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedIdeas, setExpandedIdeas] = useState(new Set());

  useEffect(() => {
    document.title = 'Collaboration | Haritanjali';
    publicAPI.getIdeas()
      .then(res => setIdeas(res.data.ideas))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(ideas.map(i => i.category).filter(Boolean))];

  const filteredIdeas = ideas.filter(idea => {
    if (categoryFilter && idea.category !== categoryFilter) return false;
    if (statusFilter && idea.status !== statusFilter) return false;
    return true;
  });

  const toggleExpand = (id) => {
    setExpandedIdeas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      {/* Hero */}
      <section style={{
        position: 'relative',
        height: '45vh',
        minHeight: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/images/hands-seedling.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(27,94,32,0.7) 100%)',
        }} />
        <div style={{
          position: 'relative', zIndex: 2, textAlign: 'center',
          color: '#fff', maxWidth: 700, padding: '0 20px',
        }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            marginBottom: 16, opacity: 0.85, fontWeight: 500,
          }}>
            Community Driven
          </p>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 800, lineHeight: 1.12,
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}>
            Collaboration Ideas
          </h1>
          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)',
            opacity: 0.9, maxWidth: 550, margin: '16px auto 0',
            lineHeight: 1.7,
          }}>
            Innovative ideas from our community to accelerate India's green revolution
          </p>
        </div>
      </section>

      {/* Ideas list */}
      <section style={{ padding: '60px 20px 80px', background: '#f5f7f5' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Filter bar */}
          {!loading && ideas.length > 0 && (
            <div style={{
              display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                style={{
                  padding: '10px 16px', border: '1.5px solid #e0e0e0', borderRadius: 10,
                  fontSize: 14, background: '#fff', color: '#333', cursor: 'pointer',
                  minWidth: 160,
                }}
              >
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{
                  padding: '10px 16px', border: '1.5px solid #e0e0e0', borderRadius: 10,
                  fontSize: 14, background: '#fff', color: '#333', cursor: 'pointer',
                  minWidth: 140,
                }}
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="featured">Featured</option>
              </select>

              {(categoryFilter || statusFilter) && (
                <button
                  onClick={() => { setCategoryFilter(''); setStatusFilter(''); }}
                  style={{
                    padding: '10px 20px', background: '#fff', border: '1.5px solid #e0e0e0',
                    borderRadius: 10, fontSize: 14, cursor: 'pointer', color: '#e65100', fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                >
                  Clear Filters
                </button>
              )}

              <span style={{ color: '#999', fontSize: 13, marginLeft: 'auto' }}>
                {filteredIdeas.length} idea{filteredIdeas.length !== 1 ? 's' : ''} found
              </span>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#888', fontSize: 17 }}>
              Loading ideas...
            </div>
          ) : ideas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>💡</div>
              <h3 style={{ color: '#1b5e20', fontSize: 24, marginBottom: 10 }}>No Ideas Yet</h3>
              <p style={{ color: '#888', fontSize: 16, marginBottom: 28 }}>
                Be the first to share your collaboration idea!
              </p>
              <Link to="/register" style={{
                display: 'inline-block',
                background: '#2e7d32', color: '#fff',
                padding: '12px 32px', borderRadius: 30,
                fontSize: 15, fontWeight: 600,
                textDecoration: 'none',
              }}>
                Register to Contribute
              </Link>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <h3 style={{ color: '#1b5e20', fontSize: 20, marginBottom: 8 }}>No Matching Ideas</h3>
              <p style={{ color: '#888', fontSize: 15 }}>
                Try adjusting your filters to see more results.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 24,
            }}>
              {filteredIdeas.map(idea => {
                const isExpanded = expandedIdeas.has(idea.id);
                const isLong = idea.description && idea.description.length > 200;
                return (
                  <div
                    key={idea.id}
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 28,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      borderLeft: `4px solid ${idea.status === 'featured' ? '#ff9800' : '#2e7d32'}`,
                      transition: 'all 0.3s ease',
                      display: 'flex', flexDirection: 'column',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
                    }}
                  >
                    {/* Featured badge + category */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      {idea.status === 'featured' && (
                        <span style={{
                          display: 'inline-block',
                          background: '#fff3e0', color: '#e65100',
                          borderRadius: 20, padding: '3px 12px',
                          fontSize: 11, fontWeight: 700,
                          letterSpacing: 0.5,
                        }}>
                          ⭐ FEATURED
                        </span>
                      )}
                      {idea.category && (
                        <span style={{
                          display: 'inline-block',
                          background: '#e8f5e9', color: '#2e7d32',
                          borderRadius: 20, padding: '3px 12px',
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {idea.category}
                        </span>
                      )}
                    </div>

                    <h4 style={{
                      color: '#1b5e20', marginBottom: 12,
                      fontSize: 18, fontWeight: 700,
                    }}>
                      {idea.title}
                    </h4>

                    <p style={{
                      color: '#666', fontSize: 15, lineHeight: 1.7,
                      marginBottom: 12, flex: 1,
                    }}>
                      {isExpanded || !isLong
                        ? idea.description
                        : idea.description.substring(0, 200) + '...'}
                    </p>

                    {/* See More / See Less */}
                    {isLong && (
                      <button
                        onClick={() => toggleExpand(idea.id)}
                        style={{
                          background: 'none', border: 'none', color: '#2e7d32',
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          padding: 0, marginBottom: 14, textAlign: 'left',
                          textDecoration: 'underline',
                        }}
                      >
                        {isExpanded ? 'See Less' : 'See More'}
                      </button>
                    )}

                    {/* Collaborator info */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      paddingTop: 14, borderTop: '1px solid #f0f0f0',
                    }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: '50%',
                        overflow: 'hidden', flexShrink: 0,
                      }}>
                        {idea.poster_photo ? (
                          <img
                            src={idea.poster_photo}
                            alt={idea.poster_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%', height: '100%',
                            background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                            color: '#2e7d32',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 700,
                          }}>
                            {idea.poster_name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
                          {idea.poster_name}
                        </div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {new Date(idea.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '70px 20px',
        background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: 'clamp(26px, 4vw, 40px)',
          color: '#fff', fontWeight: 800, marginBottom: 16,
        }}>
          Have an Idea?
        </h2>
        <p style={{
          fontSize: 17, color: 'rgba(255,255,255,0.85)',
          maxWidth: 500, margin: '0 auto 32px', lineHeight: 1.7,
        }}>
          Register as an individual and share your collaboration ideas with the community.
        </p>
        <Link to="/register" style={{
          display: 'inline-block',
          background: '#ff9800', color: '#fff',
          border: 'none', padding: '14px 40px',
          fontSize: 16, fontWeight: 700,
          borderRadius: 50,
          boxShadow: '0 4px 20px rgba(255,152,0,0.4)',
          textDecoration: 'none',
        }}>
          Register & Contribute
        </Link>
      </section>
    </>
  );
}
